// Copyright (c) Docugami, Inc. All rights reserved.

import { DgDirectory, DgFile, FileManagerInstance, FileProperties } from "filemanager/FileManager";
import { ClusterDocument } from "networking/api";
import { addToCluster, removeFromCluster } from "store/clusters/actions";
import { appendDocument } from "store/documents/actions";
import { InitialDocState } from "store/documents/types";
import { useEventListener } from "./EventListener";
import { useAppDispatch } from 'hooks/redux';
import { AppDispatch, AppState } from "store";

/**
 * File management is now performed outside of Redux. This means that changes to files that affect other
 * redux state do not get updated by the FileManager. This hook listens to FileManager events and dispatches
 * the associated changes to redux appropriately.
 */
export function useFileManagerReduxInterop() {
    useEventListener(FileManagerInstance.onItemAdded, onItemAdded);
    useEventListener(FileManagerInstance.onItemRemoved, onItemRemoved);
    useEventListener(FileManagerInstance.onFileUpdated, onFileUpdated);

    const dispatch = useAppDispatch();

    function onItemAdded(item: DgFile | DgDirectory) {
        if (item instanceof DgFile && !!item.properties.folderId) {
            dispatch(addToCluster(item.properties.clusterId!, [item.properties.folderId]));
        }
    }

    function onItemRemoved(item: DgFile | DgDirectory) {
        if (item instanceof DgFile && !!item.properties.folderId) {
            // Item with a previous doc set was removed
            dispatch(removeFromCluster(item.properties.clusterId!, [item.properties.folderId]));
        }
    }

    function onFileUpdated(updatedEvent: [FileProperties, FileProperties]) {
        const [oldProperties, newProperties] = updatedEvent;

        if (oldProperties.clusterId !== newProperties.clusterId) {
            // File has changed doc sets
            const existingDocument = dispatch(getExistingDocument(oldProperties.folderId ?? ''));
            if (!!oldProperties.clusterId) {
                // Remove from old doc set
                dispatch(removeFromCluster(oldProperties.clusterId, [oldProperties.folderId!]));
            }

            if (!!newProperties.clusterId) {
                // Add a temp doc to the target cluster - this will be replaced by the real one in the case this operation succeeds
                const tempDocument = createClusterDocForFileProperties(existingDocument, newProperties);
                dispatch(addToCluster(newProperties.clusterId!, [newProperties.folderId!]))
                dispatch(appendDocument(InitialDocState(tempDocument)));
            }
        }
    }
}

function createClusterDocForFileProperties(existingDocument: ClusterDocument | undefined, properties: FileProperties): ClusterDocument {
    return {
        id: existingDocument?.fileId ?? properties.fileId,
        clusterId: properties.clusterId!,
        folderId: properties.folderId!,
        filename: existingDocument?.filename ?? properties.name,
        fileId: properties.path,
        hasCpml: false,
        status: properties.status,
        numberOfPages: existingDocument?.numberOfPages ?? 0,
        displayName: properties.displayName,
        ingestionIssues: properties.ingestionIssues,
        ingestionWarnings: properties.ingestionWarnings,
        ingestionStatus: properties.ingestionStatus,
        metadataId: properties.fileId
    }
}

const getExistingDocument = (folderId: string) => (dispatch: AppDispatch, getState: () => AppState): ClusterDocument | undefined =>
    getState().documents.indexed[folderId]?.clusterDocument
